import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CONFIRM_REQUIRED, type DispatchBoardRow, type DispatchVehicleDay, type Shift, type ShiftEmployee, type Vehicle } from '@kontor/shared';
import { api, ApiError, queryString } from '../../api';
import { useSession } from '../../session';
import { dateDe } from './format';

/** Was das Kontextmenü an der Seite auslösen kann: Bearbeiten und Veröffentlichen laufen über die Auswahl der Seite, Löschen über deren Rückfrage */
export interface ShiftMenuActions {
  edit: (shift: Shift) => void;
  publish: (shift: Shift) => void;
  remove: (shifts: Shift[]) => void;
  /** Nach Aufgabe, Fahrzeug oder Auftrag: Plan neu laden und eine Meldung zeigen */
  done: (notice: string) => void;
}

type Dialog = 'task' | 'vehicle' | 'order' | null;

/**
 * Kontextmenü einer Schicht (Rechtsklick oder Kontextmenü-Taste). Es zeigt nur, was der Benutzer mit seinen Rechten darf: Mitarbeiter öffnen, Schicht bearbeiten oder veröffentlichen,
 * eine Aufgabe in den Dienst legen, ein Fahrzeug oder einen Auftrag zuweisen und die Schicht löschen. So lässt sich der Tag direkt im Schichtplan organisieren.
 */
export function ShiftMenu({ shift, x, y, employee, actions, onClose }: { shift: Shift; x: number; y: number; employee: ShiftEmployee | undefined; actions: ShiftMenuActions; onClose: () => void }) {
  const { can, hasModule, session } = useSession();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const [dialog, setDialog] = useState<Dialog>(null);

  const items: { key: string; label: string; hint?: string; disabled?: boolean; danger?: boolean; run: () => void }[] = [];
  if (can('hr.read')) items.push({ key: 'employee', label: 'Mitarbeiter öffnen', run: () => navigate(`/mitarbeiter/${shift.employeeId}`) });
  if (can('shifts.write')) items.push({ key: 'edit', label: 'Schicht bearbeiten', run: () => actions.edit(shift) });
  if (can('shifts.write') && shift.status === 'draft') items.push({ key: 'publish', label: 'Veröffentlichen', run: () => actions.publish(shift) });
  if (hasModule('calendar') && can('calendar.write')) {
    const noUser = !employee?.userId;
    items.push({ key: 'task', label: 'Aufgabe im Dienst anlegen', hint: noUser ? 'Nur für Mitarbeiter mit Zugang zu Wirkia' : undefined, disabled: noUser, run: () => setDialog('task') });
  }
  if (hasModule('fleet') && can('fleet.write')) items.push({ key: 'vehicle', label: 'Fahrzeug zuweisen', run: () => setDialog('vehicle') });
  if (session?.tenant.industry === 'chauffeur' && hasModule('orders') && can('orders.plan')) items.push({ key: 'order', label: 'Auftrag zuweisen', run: () => setDialog('order') });
  if (can('shifts.delete')) items.push({ key: 'delete', label: 'Löschen', danger: true, run: () => actions.remove([shift]) });

  // Im Fenster halten: Öffnet sich das Menü am rechten oder unteren Rand, rückt es nach innen
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || dialog) return;
    const r = el.getBoundingClientRect();
    setPos({ left: Math.max(8, Math.min(x, window.innerWidth - r.width - 8)), top: Math.max(8, Math.min(y, window.innerHeight - r.height - 8)) });
    el.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus({ preventScroll: true });
  }, [x, y, dialog]);

  // Schließen mit Escape, Klick daneben oder Scrollen
  useEffect(() => {
    if (dialog) return;
    const away = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        const buttons = [...(ref.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])];
        const i = buttons.indexOf(document.activeElement as HTMLButtonElement);
        buttons[(i + (e.key === 'ArrowDown' ? 1 : -1) + buttons.length) % buttons.length]?.focus();
      }
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', onClose, true);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', onClose, true);
    };
  }, [dialog, onClose]);

  if (dialog === 'task') return <TaskDialog shift={shift} employee={employee!} onClose={onClose} onDone={actions.done} />;
  if (dialog === 'vehicle') return <VehicleDialog shift={shift} onClose={onClose} onDone={actions.done} />;
  if (dialog === 'order') return <OrderDialog shift={shift} onClose={onClose} onDone={actions.done} />;

  return (
    <div ref={ref} className="shift-menu" role="menu" aria-label={`Schicht von ${shift.employeeName}`} style={{ left: pos.left, top: pos.top }}>
      <div className="shift-menu__head">
        <strong>{shift.employeeName}</strong>
        <span className="muted small">{dateDe(shift.date)}, {shift.startTime}–{shift.endTime} Uhr{shift.status === 'draft' ? ', Entwurf' : ''}</span>
      </div>
      {items.length === 0 && <p className="muted small shift-menu__empty">Für diese Schicht haben Sie keine Aktionen.</p>}
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="menuitem"
          className={`shift-menu__item${item.danger ? ' is-danger' : ''}`}
          disabled={item.disabled}
          title={item.hint}
          onClick={() => {
            item.run();
            if (item.key !== 'task' && item.key !== 'vehicle' && item.key !== 'order') onClose();
          }}
        >
          {item.label}
          {item.hint && <span className="muted small">{item.hint}</span>}
        </button>
      ))}
    </div>
  );
}

/** Modalfenster im Stil des Backoffice (<dialog>): Fokus bleibt im Fenster, Escape und Klick daneben schließen, solange nichts gespeichert wird */
export function ShiftDialog({ title, busy = false, onClose, children }: { title: string; busy?: boolean; onClose: () => void; children: ReactNode }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (el && !el.open) el.showModal();
  }, []);
  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby="shift-dialog-title"
      onClose={onClose}
      onCancel={(e) => {
        if (busy) e.preventDefault();
      }}
      onClick={(e) => {
        if (e.target === ref.current && !busy) onClose();
      }}
    >
      <div className="dialog__body">
        <h2 id="shift-dialog-title">{title}</h2>
        {children}
      </div>
    </dialog>
  );
}

/** Rückfrage vor dem Löschen einer oder mehrerer Schichten. Auch veröffentlichte werden entfernt, der Mitarbeiter sieht sie danach nicht mehr in seinem Plan. */
export function DeleteShiftsDialog({ shifts, busy, error, onConfirm, onClose }: { shifts: Shift[]; busy: boolean; error: string | null; onConfirm: () => void; onClose: () => void }) {
  const published = shifts.filter((s) => s.status === 'published').length;
  const one = shifts.length === 1 ? shifts[0]! : null;
  return (
    <ShiftDialog title={one ? 'Schicht löschen?' : `${shifts.length} Schichten löschen?`} busy={busy} onClose={onClose}>
      <p>{one ? `Die Schicht von ${one.employeeName} am ${dateDe(one.date)}, ${one.startTime} bis ${one.endTime} Uhr, wird gelöscht.` : `Die ${shifts.length} ausgewählten Schichten werden gelöscht.`}</p>
      {published > 0 && <p className="muted small">{one ? 'Die Schicht ist veröffentlicht. Der Mitarbeiter sieht sie danach nicht mehr in seinem Plan.' : `${published} davon ${published === 1 ? 'ist' : 'sind'} veröffentlicht. Die Mitarbeiter sehen sie danach nicht mehr in ihrem Plan.`} Zugewiesene Aufträge und Fahrzeuge bleiben bestehen.</p>}
      <p className="muted small">Das lässt sich nicht rückgängig machen.</p>
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <div className="form-actions">
        <button type="button" className="btn btn--danger" disabled={busy} onClick={onConfirm}>{busy ? 'Wird gelöscht …' : 'Löschen'}</button>
        <button type="button" className="btn btn--secondary" disabled={busy} onClick={onClose}>Abbrechen</button>
      </div>
    </ShiftDialog>
  );
}

const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? Object.values(e.fields)[0] ?? e.message : fallback);

/**
 * Aufgabe im Dienst: Sie gehört dem Benutzer des Mitarbeiters und liegt am Tag der Schicht, vorbelegt mit deren Zeiten. Ist die Schicht veröffentlicht, legt Wirkia die Aufgabe
 * danach in den Dienst (POST /shifts/from-task), sie erscheint dann in der Schicht. Bei einem Entwurf bleibt sie eine Aufgabe im Kalender.
 */
function TaskDialog({ shift, employee, onClose, onDone }: { shift: Shift; employee: ShiftEmployee; onClose: () => void; onDone: (notice: string) => void }) {
  const [title, setTitle] = useState('');
  const [start, setStart] = useState(shift.startTime);
  const [end, setEnd] = useState(shift.endTime);
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: async () => {
      const task = (await api<{ data: { id: string } }>('POST', '/tasks', { title, description: description || undefined, startDate: shift.date, endDate: shift.date, dueDate: shift.date, startTime: start, endTime: end, assignedUserId: employee.userId })).data;
      if (shift.status !== 'published') return 'calendar';
      const placed = await api<{ data: { fits: unknown[] } }>('POST', '/shifts/from-task', { taskId: task.id, confirm: true });
      return placed.data.fits.length > 0 ? 'shift' : 'calendar';
    },
    onSuccess: (where) => {
      onDone(where === 'shift' ? `Aufgabe „${title}“ liegt im Dienst von ${shift.employeeName}.` : `Aufgabe „${title}“ für ${shift.employeeName} angelegt. ${shift.status === 'draft' ? 'Sie erscheint in der Schicht, sobald diese veröffentlicht ist.' : 'Sie liegt außerhalb der Schichtzeit und steht deshalb nur im Kalender.'}`);
      onClose();
    },
    onError: (e) => setError(errorText(e, 'Die Aufgabe konnte nicht angelegt werden.')),
  });
  return (
    <ShiftDialog title="Aufgabe im Dienst anlegen" busy={save.isPending} onClose={onClose}>
      <p className="muted small">Für {shift.employeeName} am {dateDe(shift.date)}. Die Aufgabe erscheint in seinem Kalender und, solange sie in die Schichtzeit passt, in der Schicht.</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        <label className="field"><span>Titel</span><input className="input" required maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} autoFocus /></label>
        <div className="form-grid">
          <label className="field"><span>Beginn</span><input className="input" type="time" required value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label className="field"><span>Ende</span><input className="input" type="time" required value={end} onChange={(e) => setEnd(e.target.value)} /></label>
        </div>
        <label className="field"><span>Beschreibung (freiwillig)</span><textarea className="input" rows={3} maxLength={5000} value={description} onChange={(e) => setDescription(e.target.value)} /></label>
        {error && <div className="alert alert--error" role="alert">{error}</div>}
        <div className="form-actions">
          <button type="submit" className="btn btn--primary" disabled={save.isPending || !title.trim()}>{save.isPending ? 'Wird angelegt …' : 'Aufgabe anlegen'}</button>
          <button type="button" className="btn btn--secondary" disabled={save.isPending} onClick={onClose}>Abbrechen</button>
        </div>
      </form>
    </ShiftDialog>
  );
}

/** Fahrzeug für die Schichtzeit einplanen (Fahrzeugeinsatz mit Fahrer). Überschneidungen meldet der Server, die Meldung steht dann im Fenster. */
function VehicleDialog({ shift, onClose, onDone }: { shift: Shift; onClose: () => void; onDone: (notice: string) => void }) {
  const vehicles = useQuery({ queryKey: ['fleet-vehicles-active'], queryFn: async () => (await api<{ data: Vehicle[] }>('GET', `/fleet/vehicles${queryString({ status: 'active', perPage: 100 })}`)).data });
  const [vehicleId, setVehicleId] = useState('');
  const [start, setStart] = useState(shift.startTime);
  const [end, setEnd] = useState(shift.endTime);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => api('POST', '/fleet/assignments', { vehicleId, employeeId: shift.employeeId, startsAt: `${shift.date}T${start}`, endsAt: `${shift.date}T${end}`, note: null }),
    onSuccess: () => {
      const v = vehicles.data?.find((x) => x.id === vehicleId);
      onDone(`${v?.plate ?? 'Fahrzeug'} ist für ${shift.employeeName} am ${dateDe(shift.date)} von ${start} bis ${end} Uhr eingeplant.`);
      onClose();
    },
    onError: (e) => setError(errorText(e, 'Das Fahrzeug konnte nicht eingeplant werden.')),
  });
  return (
    <ShiftDialog title="Fahrzeug zuweisen" busy={save.isPending} onClose={onClose}>
      <p className="muted small">{shift.employeeName} bekommt das Fahrzeug für die gewählte Zeit am {dateDe(shift.date)}. Es erscheint in der Flotte als eingeplant.</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        <label className="field">
          <span>Fahrzeug</span>
          <select className="input" required value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
            <option value="">{vehicles.isLoading ? 'Wird geladen …' : 'Bitte wählen'}</option>
            {(vehicles.data ?? []).map((v) => <option key={v.id} value={v.id}>{v.plate}{v.name || v.model ? ` · ${v.name ?? v.model}` : ''}</option>)}
          </select>
        </label>
        <div className="form-grid">
          <label className="field"><span>Von</span><input className="input" type="time" required value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label className="field"><span>Bis</span><input className="input" type="time" required value={end} onChange={(e) => setEnd(e.target.value)} /></label>
        </div>
        {vehicles.data?.length === 0 && <p className="muted small">Es gibt kein Fahrzeug im Betrieb.</p>}
        {error && <div className="alert alert--error" role="alert">{error}</div>}
        <div className="form-actions">
          <button type="submit" className="btn btn--primary" disabled={save.isPending || !vehicleId}>{save.isPending ? 'Wird eingeplant …' : 'Fahrzeug zuweisen'}</button>
          <button type="button" className="btn btn--secondary" disabled={save.isPending} onClick={onClose}>Abbrechen</button>
        </div>
      </form>
    </ShiftDialog>
  );
}

const plusHour = (time: string) => {
  const [h, m] = time.split(':').map(Number) as [number, number];
  const total = Math.min(h * 60 + m + 60, 23 * 60 + 59);
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
};

/**
 * Auftrag zuweisen (Chauffeur-Service): ein offener Auftrag ohne Fahrer an diesem Tag, dazu ein Fahrzeug. Die Disposition prüft Ruhezeit, Überschneidungen und Fahrzeugklasse.
 * Was dagegen spricht, kommt als Warnung zurück, der Benutzer kann es bestätigen und trotzdem zuweisen.
 */
function OrderDialog({ shift, onClose, onDone }: { shift: Shift; onClose: () => void; onDone: (notice: string) => void }) {
  const board = useQuery({ queryKey: ['dispatch-board', 'unassigned'], queryFn: async () => (await api<{ data: { rows: DispatchBoardRow[] } }>('GET', '/orders/dispatch/board?scope=unassigned')).data.rows });
  const fleet = useQuery({ queryKey: ['dispatch-vehicles', shift.date], queryFn: async () => (await api<{ data: DispatchVehicleDay[] }>('GET', `/orders/dispatch/vehicles?date=${shift.date}`)).data });
  const rows = (board.data ?? []).filter((r) => r.date === shift.date);
  const [orderId, setOrderId] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const chosen = rows.find((r) => r.id === orderId);
  const [start, setStart] = useState(shift.startTime);
  const [end, setEnd] = useState(plusHour(shift.startTime));
  const [warnings, setWarnings] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (confirmWarnings: boolean) => api('POST', `/orders/${orderId}/dispatch`, { employeeId: shift.employeeId, vehicleId, startsAt: `${shift.date}T${start}`, endsAt: `${shift.date}T${end}`, notifyCustomer: true, confirmWarnings }),
    onSuccess: () => {
      onDone(`Auftrag ${chosen?.number ?? ''} ist ${shift.employeeName} zugewiesen.`);
      onClose();
    },
    onError: (e) => {
      if (e instanceof ApiError && e.code === CONFIRM_REQUIRED) setWarnings(Object.values(e.fields));
      else setError(errorText(e, 'Der Auftrag konnte nicht zugewiesen werden.'));
    },
  });
  const pickOrder = (id: string) => {
    setOrderId(id);
    const r = rows.find((x) => x.id === id);
    if (r?.time) {
      setStart(r.time);
      setEnd(plusHour(r.time));
    }
  };
  return (
    <ShiftDialog title="Auftrag zuweisen" busy={save.isPending} onClose={onClose}>
      <p className="muted small">Offene Aufträge ohne Fahrer am {dateDe(shift.date)}. {shift.employeeName} fährt den Auftrag mit dem gewählten Fahrzeug, der Kunde bekommt die Angaben zu Fahrer und Fahrzeug per E-Mail.</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          setWarnings(null);
          save.mutate(false);
        }}
      >
        <label className="field">
          <span>Auftrag</span>
          <select className="input" required value={orderId} onChange={(e) => pickOrder(e.target.value)}>
            <option value="">{board.isLoading ? 'Wird geladen …' : rows.length === 0 ? 'Kein offener Auftrag an diesem Tag' : 'Bitte wählen'}</option>
            {rows.map((r) => <option key={r.id} value={r.id}>{r.time ? `${r.time} · ` : ''}{r.number} · {r.subject}</option>)}
          </select>
        </label>
        <label className="field">
          <span>Fahrzeug</span>
          <select className="input" required value={vehicleId} onChange={(e) => setVehicleId(e.target.value)}>
            <option value="">{fleet.isLoading ? 'Wird geladen …' : 'Bitte wählen'}</option>
            {(fleet.data ?? []).filter((v) => v.state !== 'unavailable').map((v) => <option key={v.vehicleId} value={v.vehicleId}>{v.plate} · {v.label}{v.state === 'busy' ? ' (an dem Tag schon eingeplant)' : ''}</option>)}
          </select>
        </label>
        <div className="form-grid">
          <label className="field"><span>Beginn</span><input className="input" type="time" required value={start} onChange={(e) => setStart(e.target.value)} /></label>
          <label className="field"><span>Ende</span><input className="input" type="time" required value={end} onChange={(e) => setEnd(e.target.value)} /></label>
        </div>
        {warnings && (
          <div className="alert alert--warn" role="alert">
            <ul className="shift-menu__warnings">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
            <button type="button" className="btn btn--secondary btn--sm" disabled={save.isPending} onClick={() => save.mutate(true)}>Trotzdem zuweisen</button>
          </div>
        )}
        {error && <div className="alert alert--error" role="alert">{error}</div>}
        <div className="form-actions">
          <button type="submit" className="btn btn--primary" disabled={save.isPending || !orderId || !vehicleId}>{save.isPending ? 'Wird zugewiesen …' : 'Auftrag zuweisen'}</button>
          <button type="button" className="btn btn--secondary" disabled={save.isPending} onClick={onClose}>Abbrechen</button>
        </div>
      </form>
    </ShiftDialog>
  );
}
